// Pure helpers shared by the whole-home Holiday controls (the dedicated Holiday
// screen). The accepted end time is tomorrow to 90
// days out; these turn that window into the values a native
// <input type="datetime-local"> needs, and validate the chosen value against the
// same window.
//
// Validity is computed off a fresh `now` at call time — deliberately NOT delegated
// to darwin_core's holidayEndTimeValidator, whose min/max are module-level consts
// frozen when the module first loads. In this long-lived SPA (a tab is never torn
// down between screens) that frozen window drifts: after a day of uptime the real
// "+24h/+90d" bounds no longer match, and a date the picker shows as in-range would
// be silently rejected with the Start button disabled and no explanation; after 90
// days Start breaks entirely. Recomputing here keeps the button state honest and in
// step with holidayPickerBounds. (darwin_core's own validator has the same latent
// bug for the mobile app, but that is masked by app restarts — flagged separately.)

const DAY_MS = 24 * 60 * 60 * 1000;

// Format a Date as the value a datetime-local input expects (local wall-clock,
// minute precision), so min/max/default all round-trip through the picker.
export function toLocalInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

// The picker bounds, mirroring the validator: earliest tomorrow, latest 90 days
// out, default a week away. `now` (a millisecond timestamp) is injected so the
// caller can freeze it at mount and so the maths is testable without the clock.
export function holidayPickerBounds(now) {
  return {
    minValue: toLocalInputValue(new Date(now + DAY_MS)),
    maxValue: toLocalInputValue(new Date(now + 90 * DAY_MS)),
    defaultValue: toLocalInputValue(new Date(now + 7 * DAY_MS)),
  };
}

// Whether a datetime-local string is an acceptable holiday end: parse the picker
// value and check it falls within the fresh [now+1 day, now+90 days] window
// (inclusive, matching the picker bounds and darwin_core's yup .min/.max). An empty
// or unparseable value is invalid. `now` (ms) is injectable for testing; it
// defaults to the real clock so the window is always current.
export function isValidHolidayEnd(endValue, now = Date.now()) {
  if (!endValue) {
    return false;
  }
  const time = new Date(endValue).getTime();
  if (Number.isNaN(time)) {
    return false;
  }
  return time >= now + DAY_MS && time <= now + 90 * DAY_MS;
}

// A holiday's end as shown to the user — "5 Jun 2030, 13:00" (en-GB, date + time).
// `end` is whatever the store holds: an ISO string from the wire or a Date from an
// optimistic pending request. Returns '' for a blank or unparseable end so callers
// drop the "until …" clause rather than print "Invalid Date".
export function formatHolidayEnd(end) {
  if (!end) {
    return '';
  }
  const date = new Date(end);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
